import { Helmet } from 'react-helmet-async'
import { FadeIn } from '@components/animations/FadeIn/FadeIn'
import { Search, PenTool, Code, CheckCircle, Rocket, RefreshCcw } from 'lucide-react'

const phases = [
  {
    icon: Search,
    title: '1. Discovery & Analysis',
    description: 'Understanding requirements, stakeholder mapping, current state assessment, and defining success metrics.',
    deliverables: ['Requirement Document', 'Stakeholder Map', 'Current State Report', 'Success Metrics'],
  },
  {
    icon: PenTool,
    title: '2. Design & Architecture',
    description: 'Solution architecture, UI/UX design, technical specifications, and prototype development.',
    deliverables: ['Solution Architecture', 'UI/UX Designs', 'Technical Specs', 'Working Prototype'],
  },
  {
    icon: Code,
    title: '3. Development',
    description: 'Agile development with sprints, continuous integration, code reviews, and quality checks.',
    deliverables: ['Source Code', 'Unit Tests', 'API Documentation', 'Code Review Reports'],
  },
  {
    icon: CheckCircle,
    title: '4. Testing & QA',
    description: 'Comprehensive testing including functional, integration, performance, security, and UAT.',
    deliverables: ['Test Cases', 'Bug Reports', 'Performance Metrics', 'Security Audit'],
  },
  {
    icon: Rocket,
    title: '5. Deployment',
    description: 'Phased rollout, data migration, user training, go-live support, and hypercare.',
    deliverables: ['Deployment Plan', 'Migration Scripts', 'Training Materials', 'Go-Live Report'],
  },
  {
    icon: RefreshCcw,
    title: '6. Maintenance & Support',
    description: '24/7 support, regular updates, performance monitoring, and continuous improvement.',
    deliverables: ['SLA Reports', 'Incident Logs', 'Release Notes', 'Improvement Roadmap'],
  },
]

const Methodology = () => {
  return (
    <>
      <Helmet>
        <title>Project Methodology | Eonixa Dynamics</title>
        <meta name="description" content="Our proven project methodology - Discovery, Design, Development, Testing, Deployment & Support" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 pt-32 pb-20">
        <div className="section-container">
          <FadeIn>
            <div className="text-center mb-16">
              <h1 className="text-4xl font-bold text-gray-900">Project Methodology</h1>
              <p className="mt-4 text-lg text-gray-600 max-w-3xl mx-auto">
                Our proven 6-phase methodology ensures successful project delivery
                with quality, transparency, and timely execution.
              </p>
            </div>
          </FadeIn>

          {/* Methodology Phases */}
          <div className="relative">
            {/* Connection Line */}
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-primary-200 hidden lg:block" />

            <div className="space-y-12">
              {phases.map((phase, index) => {
                const Icon = phase.icon
                return (
                  <FadeIn key={phase.title} delay={index * 0.1}>
                    <div className="relative flex gap-8">
                      {/* Icon */}
                      <div className="hidden lg:flex flex-shrink-0 h-16 w-16 items-center justify-center rounded-full bg-primary-600 text-white ring-4 ring-white z-10">
                        <Icon className="h-7 w-7" />
                      </div>

                      {/* Content */}
                      <div className="flex-1 rounded-2xl bg-white p-8 shadow-soft">
                        <div className="flex items-center gap-4 mb-4 lg:hidden">
                          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary-50 text-primary-600">
                            <Icon className="h-6 w-6" />
                          </div>
                          <h3 className="text-xl font-bold text-gray-900">
                            {phase.title}
                          </h3>
                        </div>
                        <h3 className="hidden lg:block mb-4 text-xl font-bold text-gray-900">
                          {phase.title}
                        </h3>
                        <p className="text-gray-600 mb-6">{phase.description}</p>
                        <div>
                          <p className="text-sm font-semibold text-gray-700 mb-3">
                            Key Deliverables:
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {phase.deliverables.map((deliverable) => (
                              <span
                                key={deliverable}
                                className="inline-flex items-center gap-1 rounded-full bg-primary-50 px-3 py-1 text-sm text-primary-700"
                              >
                                <CheckCircle className="h-3 w-3" />
                                {deliverable}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </FadeIn>
                )
              })}
            </div>
          </div>

          <FadeIn delay={0.6} className="mt-16">
            <div className="rounded-2xl bg-gradient-to-r from-primary-900 to-primary-700 p-12 text-center text-white">
              <h2 className="mb-4 text-2xl font-bold">Agile + Waterfall Hybrid</h2>
              <p className="mx-auto max-w-2xl text-gray-200 mb-8">
                We follow a hybrid approach combining Agile flexibility with Waterfall
                structure - perfect for government projects requiring documentation
                and iterative development.
              </p>
              <div className="flex flex-wrap justify-center gap-8">
                <div className="text-center">
                  <p className="text-4xl font-bold text-accent-400">100%</p>
                  <p className="text-sm text-gray-300">On-Time Delivery</p>
                </div>
                <div className="text-center">
                  <p className="text-4xl font-bold text-accent-400">98%</p>
                  <p className="text-sm text-gray-300">Client Satisfaction</p>
                </div>
                <div className="text-center">
                  <p className="text-4xl font-bold text-accent-400">50+</p>
                  <p className="text-sm text-gray-300">Projects Delivered</p>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </div>
    </>
  )
}

export default Methodology
